<template>
  <aside class="sbar" :class="{ open: open, 'sbar-mini': collapsed }">
    <div class="sbar-in">
      <button
        class="sb-toggle only-desktop"
        type="button"
        :title="collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'"
        :aria-label="collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'"
        :aria-expanded="collapsed ? 'false' : 'true'"
        @click="$emit('toggle')"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path v-if="collapsed" d="M9 6l6 6-6 6" />
          <path v-else d="M15 6l-6 6 6 6" />
        </svg>
      </button>
      <router-link
        class="sbar-brand"
        :to="{ name: 'Event Hub', params: { id: eventId } }"
      >
        <span class="sb-title">{{ event.title }}</span>
        <span class="sb-sub">{{ subtitle }}</span>
      </router-link>

      <nav class="sbar-nav" aria-label="Sections">
        <router-link
          v-for="l in links"
          :key="l.name"
          class="sb-link"
          :class="{ on: isOn(l) }"
          :to="{ name: l.name, params: { id: eventId } }"
          :title="collapsed ? l.label : null"
          @click.native="$emit('close')"
        >
          <!-- eslint-disable-next-line vue/no-v-html -->
          <svg
            class="sb-ic"
            viewBox="0 0 24 24"
            aria-hidden="true"
            v-html="l.icon"
          ></svg>
          <span class="sb-lab">{{ l.label }}</span>
          <span v-if="l.n" class="sb-n">{{ l.n }}</span>
        </router-link>
      </nav>

      <div class="sbar-sessions">
        <div class="sb-h">{{ event.sessionsLabel || 'Sessions' }}</div>
        <router-link
          v-for="r in orderedSessions"
          :key="r.id"
          class="sb-sess"
          :class="{ on: $route.params.recId === r.id }"
          :to="{ name: 'Recording', params: { id: eventId, recId: r.id } }"
          :title="collapsed ? r.title : null"
          @click.native="$emit('close')"
        >
          <span class="sb-num" aria-hidden="true">{{ two(r._n) }}</span>
          <span class="sb-thumb" :style="thumbStyle(r)">
            <svg v-if="!posterOf(r)" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M7 5v14l11-7z" />
            </svg>
          </span>
          <span class="sb-sm">
            <span class="sb-st">{{ r.title }}</span>
            <span class="sb-sd">
              <b>{{ two(r._n) }}</b> · {{ r.durationLabel }}
            </span>
          </span>
          <svg class="sb-go" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </router-link>
      </div>

      <a
        class="sbar-cta"
        href="https://app.decast.live"
        target="_blank"
        rel="noopener"
        >Run yours on Decast →</a
      >
    </div>
    <div class="sbar-scrim only-mobile" @click="$emit('close')"></div>
  </aside>
</template>

<script>
import { posterFor } from '../useEvent';

const ICONS = {
  overview: '<path d="M4 5h16M4 10h10M4 15h16M4 20h7"/>',
  insights:
    '<path d="M11 3.5l1.6 4.3 4.3 1.6-4.3 1.6L11 15.3 9.4 11 5.1 9.4l4.3-1.6z"/><path d="M18 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"/>',
  moments:
    '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M10 9.5v5l4-2.5z"/>',
  files: '<path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"/>',
  authenticity:
    '<path d="M12 3l7 3v6c0 4.2-2.9 7.8-7 9-4.1-1.2-7-4.8-7-9V6z"/><path d="M9 12l2 2 4-4"/>',
};

export default {
  name: 'HubSidebar',
  props: {
    event: { type: Object, required: true },
    eventId: { type: String, required: true },
    dashboard: { type: Object, default: () => ({}) },
    media: { type: Object, default: () => ({}) },
    recordings: { type: Array, default: () => [] },
    hasProvenance: { type: Boolean, default: false },
    open: { type: Boolean, default: false },
    // icons-only rail (session page)
    collapsed: { type: Boolean, default: false },
  },
  computed: {
    // in the order of the day, 01 at the top
    orderedSessions() {
      return this.recordings.map((r, i) => Object.assign({ _n: i + 1 }, r));
    },
    subtitle() {
      return [this.event.dateLabel, this.event.recordedLabel]
        .filter(Boolean)
        .join(' · ');
    },
    links() {
      const m = this.media || {};
      const out = [
        { name: 'Event Hub', label: 'Overview', icon: ICONS.overview, n: 0 },
        {
          name: 'Event Insights',
          label: 'Insights',
          icon: ICONS.insights,
          n: this.dashboard.insightsTotal || 0,
        },
        {
          name: 'Event Moments',
          label: 'Moments',
          icon: ICONS.moments,
          n: (m.clips || 0) + (m.shorts || 0),
        },
        { name: 'Event Files', label: 'Files', icon: ICONS.files, n: 0 },
      ];
      if (this.hasProvenance) {
        out.push({
          name: 'Event Authenticity',
          label: 'Authenticity',
          icon: ICONS.authenticity,
          n: 0,
        });
      }
      return out;
    },
  },
  methods: {
    // Overview must not stay lit on every child route, so match it exactly
    isOn(l) {
      if (l.name === 'Event Hub') return this.$route.name === 'Event Hub';
      return this.$route.name === l.name;
    },
    posterOf(r) {
      return posterFor(r);
    },
    thumbStyle(r) {
      const u = posterFor(r);
      return u ? { backgroundImage: `url("${u}")` } : {};
    },
    signalsOf(r) {
      return ((r.analysis && r.analysis.dashboard) || {}).insightsTotal || 0;
    },
    two(n) {
      return ('0' + n).slice(-2);
    },
  },
};
</script>
